import { useState } from 'react';
import { useCallback } from 'react';

import ReadMoreArea from '@foxeian/react-read-more';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  Table,
  useReactTable,
} from '@tanstack/react-table';
import { useParams, useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { NoAnswer } from './no-answer';
import { ReferencesDict } from '../../../../../../../../../../../../backend/src/types/types';
import { cn } from '../../../../../../../../../../helpers/shadCnUtils';
import SearchBox from '@/components/molecules/SearchBox';
import { referencingLinkRegex } from '@/constants/referencing';
import { Button } from '@/new/components/shadcn/button';
import { useStore } from '@/store';

interface TextWithReferencesProps {
  text: string;
  references?: ReferencesDict;
  uniqueId: string;
}

const TextWithReferences = ({ text, references, uniqueId }: TextWithReferencesProps) => {
  const { openReferenceDrawer } = useStore();
  const [searchParams] = useSearchParams();
  const { matterId } = useParams();
  const caseId = searchParams.get('caseId') ?? matterId;

  const handleClick = useCallback(
    (ref: { type: 'fact' | 'chunk'; id: string }) => {
      if (!caseId) return;
      if (ref.type === 'chunk') {
        openReferenceDrawer({ chunkId: ref.id, caseId });
      } else {
        openReferenceDrawer({ factId: ref.id, caseId });
      }
    },
    [caseId, openReferenceDrawer],
  );

  const createReadMore = useCallback((text: string) => {
    return text.length > 200 ? (
      <ReadMoreArea key={uniqueId} lettersLimit={200}>
        {text}
      </ReadMoreArea>
    ) : (
      text
    );
  }, []);

  const renderTextWithReferences = (text: string) => {
    if (references === undefined) {
      return createReadMore(text);
    }

    const parts = [];
    let lastIndex = 0;
    let match;
    let referenceIndex = 0;

    while ((match = referencingLinkRegex.exec(text)) !== null) {
      const [fullMatch, num, uuid] = match;
      const startIndex = match.index;

      if (startIndex > lastIndex) {
        parts.push(text.substring(lastIndex, startIndex));
      }
      const ref = references[num];

      if (ref === undefined) {
        parts.push(`[${num}]`);
        lastIndex = startIndex + fullMatch.length;
        continue;
      }

      // Push the reference
      parts.push(
        <span
          className={`cursor-pointer ${ref.type === 'chunk' ? 'text-orange-500' : 'text-blue-500'}`}
          key={`${num}-${uuid}-${referenceIndex}-${uniqueId}`}
          onClick={() => handleClick(ref)}
        >
          [{num}]
        </span>,
      );

      referenceIndex++;
      lastIndex = startIndex + fullMatch.length;
    }

    // Push the remaining text after the last reference
    if (lastIndex < text.length) {
      parts.push(createReadMore(text.substring(lastIndex)));
    }

    return parts;
  };

  return <div>{renderTextWithReferences(text)}</div>;
};

interface ExtractionContentProps {
  data: Record<string, string>[];
  references?: ReferencesDict;
}

const ColumneResizer = ({ header }: { header: any }) => (
  <div
    onDoubleClick={() => header.column.resetSize()}
    onMouseDown={header.getResizeHandler()}
    onTouchStart={header.getResizeHandler()}
    className={`bg-brandPrimary hover:bg-brandSecondary absolute top-0 right-0 h-full w-px cursor-col-resize touch-none opacity-40 transition-colors select-none group-hover:w-0.5 ${
      header.column.getIsResizing() ? 'bg-brandSecondary' : ''
    }`}
  />
);

const TableData = ({
  tableInstance,
  isEmpty,
  onClearFilter,
}: {
  tableInstance: Table<Record<string, string>>;
  isEmpty: boolean;
  onClearFilter: VoidFunction;
}) => {
  if (isEmpty) {
    return (
      <tr className="mt-3 flex flex-col justify-center">
        <p className="mb-2">No data</p>
        <Button onClick={onClearFilter}>Clear filter</Button>
      </tr>
    );
  }

  return (
    <tbody className="text-sm">
      {tableInstance.getRowModel().rows.map((row) => (
        <tr key={row.id}>
          {row.getVisibleCells().map((cell) => (
            <td
              key={cell.id}
              style={{
                width: cell.column.getSize(),
              }}
              className="p-4 align-text-top"
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};

export function ExtractionContent({ data, references }: ExtractionContentProps) {
  const [tableData] = useState(data);
  const [globalFilter, setGlobalFilter] = useState('');
  const columnNames = data.length > 0 ? Object.keys(data?.[0]) : [];
  const columnHelper = createColumnHelper<Record<string, string>>();

  const columns = columnNames.map((column, columnIndex) => {
    return columnHelper.accessor(
      (row) => {
        return row[column];
      },
      {
        id: column,
        cell: (cell) => {
          const value = cell.getValue();
          const rowIndex = cell.row.index;

          if (!value) {
            return '';
          }

          return <TextWithReferences text={value} references={references} uniqueId={`${rowIndex}-${columnIndex}`} />;
        },
      },
    );
  });

  const tableInstance = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableMultiSort: false,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
  });

  const numberOfRows = tableInstance.getPrePaginationRowModel().rows.length;
  if (tableData.length === 0) {
    return <NoAnswer />;
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="mb-3 flex items-center justify-between">
        <p>{numberOfRows} rows</p>
        <SearchBox value={globalFilter} onChange={setGlobalFilter} placeholder="Search table" />
      </div>
      <div className="overflow-y-auto">
        <table className="my-0">
          <thead>
            {tableInstance.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="overflow-visible">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="group relative overflow-x-visible px-2 py-1"
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    <div
                      className={twMerge(
                        'flex items-center justify-between truncate',
                        header.column.getCanSort() ? 'cursor-pointer select-none' : '',
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && (
                        <span
                          className={cn(
                            header.column.getIsSorted() === 'asc'
                              ? 'iconify lucide--chevron-up'
                              : 'iconify lucide--chevron-down',
                            'mr-2',
                          )}
                        />
                      )}
                    </div>
                    <ColumneResizer header={header} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <TableData
            tableInstance={tableInstance}
            isEmpty={numberOfRows === 0}
            onClearFilter={() => setGlobalFilter('')}
          />
        </table>
      </div>
    </div>
  );
}
